import { useCallback, useEffect, useMemo, useRef } from 'react'
import { isEqual } from 'lodash'

import { useSearchParams } from 'modules/Monitoring/router/useSearchParams'

import { UpdateStateHandler } from './types'
import { UrlStateStorage } from './urlStateStorage'
import { decodeState, encodeState } from './utils/encodeState'
import { SearchParamsQueue } from './utils/searchParamsQueue'

export type UseHashedSearchParams<T> = {
  hashParamKey: string
  initialState?: T
  contextKey?: string
}

const searchParamsQueue = new SearchParamsQueue()

export const useHashedSearchParams = <T extends Record<string, unknown>>({
  hashParamKey,
  initialState,
  contextKey,
}: UseHashedSearchParams<T>) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const prevContextKey = useRef(contextKey)

  const storageKey = contextKey ? `${contextKey}:${hashParamKey}` : null
  const encodedStr = searchParams.get(hashParamKey)

  const state = useMemo(() => {
    const decodedState = decodeState<T>(encodedStr) || ({} as T)
    return { ...(initialState || {}), ...decodedState } as T
  }, [encodedStr, initialState])

  useEffect(
    function restoreSearchParamsWhenContextChanges() {
      if (!storageKey) {
        return
      }

      if (contextKey !== prevContextKey.current) {
        prevContextKey.current = contextKey

        const savedEncodedStr = UrlStateStorage.load(storageKey)
        const currentEncodedStr = searchParams.get(hashParamKey)

        if (savedEncodedStr && savedEncodedStr !== currentEncodedStr) {
          searchParamsQueue.schedule(
            params => params.set(hashParamKey, savedEncodedStr),
            setSearchParams,
            true,
          )
        } else if (!savedEncodedStr && currentEncodedStr) {
          searchParamsQueue.schedule(
            params => params.delete(hashParamKey),
            setSearchParams,
            true,
          )
        }
      }
    },
    [contextKey, storageKey, hashParamKey, searchParams, setSearchParams],
  )

  useEffect(
    function saveLastActiveState() {
      if (!storageKey) {
        return
      }
      const currentStr = searchParams.get(hashParamKey)
      UrlStateStorage.save(storageKey, currentStr || '')
    },
    [storageKey, searchParams, hashParamKey],
  )

  const updateState = useCallback<UpdateStateHandler<T>>(
    (updates, replaceHistory = false, strategy = 'merge') => {
      const currentState = state
      const updatedState = typeof updates === 'function' ? updates(currentState) : updates

      if (typeof updatedState === 'undefined') {
        return
      }

      const newState =
        strategy === 'merge'
          ? {
              ...currentState,
              ...updatedState,
            }
          : updatedState
      const hashedState: Record<string, unknown> = {}

      Object.entries(newState).forEach(([key, value]) => {
        if (!initialState || !isEqual(value, initialState[key])) {
          hashedState[key] = value
        }
      })

      const newEncodedStr = encodeState(hashedState)

      if (storageKey) {
        UrlStateStorage.save(storageKey, newEncodedStr)
      }

      searchParamsQueue.schedule(
        nextParams => {
          if (newEncodedStr) {
            nextParams.set(hashParamKey, newEncodedStr)
          } else {
            nextParams.delete(hashParamKey)
          }
        },
        setSearchParams,
        replaceHistory,
      )
    },
    [state, setSearchParams, hashParamKey, storageKey, initialState],
  )

  const clearState = useCallback(() => {
    if (storageKey) {
      UrlStateStorage.save(storageKey, '')
    }

    searchParamsQueue.schedule(
      nextParams => {
        nextParams.delete(hashParamKey)
      },
      setSearchParams,
      false,
    )
  }, [setSearchParams, hashParamKey, storageKey])

  const removeKeys = useCallback(
    (keys: Array<keyof T>, replaceHistory = false) => {
      updateState(
        previousState => {
          const nextState = { ...previousState }
          keys.forEach(key => {
            delete nextState[key]
          })
          return nextState
        },
        replaceHistory,
        'replace',
      )
    },
    [updateState],
  )

  return {
    state,
    updateState,
    clearState,
    removeKeys,
    currentHash: searchParams.get(hashParamKey),
  }
}
